
import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { useToast } from "@/hooks/use-toast";
import { checkGeofence } from "@/utils/geofenceUtils";
import { usePositionTracking } from "./usePositionTracking";

export const useGpsTracking = () => {
  const [tracking, setTracking] = useState(false);
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [timeRegistrationId, setTimeRegistrationId] = useState<string | null>(null);
  const [nearestLocation, setNearestLocation] = useState<any>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const { toast } = useToast();

  const handlePositionUpdate = async (pos: GeolocationPosition) => {
    console.log("GPS position modtaget:", {
      lat: pos.coords.latitude,
      lng: pos.coords.longitude,
      acc: pos.coords.accuracy
    });
    
    setPosition(pos);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.id) {
        throw new Error("Ingen bruger fundet");
      }

      const { isInside, location } = await checkGeofence(
        pos.coords.latitude,
        pos.coords.longitude
      );

      setNearestLocation(location);

      const { error: locationError } = await supabase.from("location_logs").insert({
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        user_id: user.id,
        is_inside_geofence: isInside,
        work_location_id: location?.id
      });

      if (locationError) {
        console.error("Fejl ved logging af position:", locationError);
        throw locationError;
      }

      if (isInside && !timeRegistrationId && tracking && currentTaskId) {
        const { data: registration, error: registrationError } = await supabase
          .from("time_registrations")
          .insert({
            user_id: user.id,
            start_time: new Date().toISOString(),
            status: 'active',
            work_location_id: location.id,
            task_id: currentTaskId,
            registration_type: 'gps'
          })
          .select()
          .single();

        if (registrationError) {
          throw registrationError;
        }

        if (registration) {
          setTimeRegistrationId(registration.id);
          toast({
            title: "Tidsregistrering startet",
            description: `Din arbejdstid bliver nu registreret ved ${location.title}`,
          });
        }
      }

      if (!isInside && timeRegistrationId && tracking) {
        toast({
          title: "Udenfor arbejdsområde",
          description: "Du har bevæget dig uden for arbejdsområdet",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Fejl ved logging af position:", error);
      toast({
        title: "Fejl ved positionslogging",
        description: "Kunne ikke gemme position i databasen. Prøv igen.",
        variant: "destructive",
      });
    }
  };

  const { startTracking: startGpsTracking, stopTracking: stopGpsTracking } = usePositionTracking(handlePositionUpdate);

  const startTracking = async (taskId: string) => {
    if (!taskId) {
      toast({
        title: "Vælg opgave",
        description: "Du skal vælge en opgave før du kan starte tidsregistrering",
        variant: "destructive",
      });
      return;
    }
    setCurrentTaskId(taskId);
    const success = startGpsTracking();
    if (success) {
      setTracking(true);
    }
  };

  const stopTracking = async () => {
    if (timeRegistrationId) {
      try {
        const { error } = await supabase
          .from("time_registrations")
          .update({
            end_time: new Date().toISOString(),
            status: 'completed'
          })
          .eq('id', timeRegistrationId);

        if (error) throw error;

        setTimeRegistrationId(null);
        setCurrentTaskId(null);
        toast({
          title: "Tidsregistrering afsluttet",
          description: "Din arbejdstid er blevet gemt",
        });
      } catch (error) {
        console.error("Fejl ved afslutning af tidsregistrering:", error);
        toast({
          title: "Fejl ved afslutning",
          description: "Kunne ikke gemme sluttidspunkt. Kontakt support.",
          variant: "destructive",
        });
      }
    }

    stopGpsTracking();
    setTracking(false);
    setNearestLocation(null);
    toast({
      title: "GPS Tracking stoppet",
      description: "Din position bliver ikke længere registreret",
    });
  };

  return {
    tracking,
    position,
    nearestLocation,
    startTracking,
    stopTracking
  };
};
